import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Ban, Check, PackageCheck, Pencil, Send, Trash2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Carregando, Erro } from '../../componentes/estados';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dataHora, dinheiro, nomeCadastro, numero, textoLivre } from '../../nucleo/formato';
import { useSessaoFluxo } from './AvisoModo';
import { Linha, Secao } from './comum';
import { FormPedido } from './FormPedido';
import { ModalRecebimento } from './ModalRecebimento';
import { COR_PEDIDO, invalidarCompras, qtd, type PedidoDetalhe } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';
import { anunciarOcultos } from '../../componentes/sensivel';
import { AreaSensivel } from '../../componentes/formulario';
import { LinkParceiro } from '../../componentes/link-parceiro';
import { useCamposDaTela } from '../../nucleo/campos-tela';

function Progresso({ feito, total }: { feito: number; total: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((feito / total) * 100)) : 0;
  return (
    <div role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} className="h-1.5 w-full overflow-hidden rounded-full bg-sup-3">
      <div className="h-full rounded-full bg-acento-forte" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Ficha do pedido (M10.1): itens com o recebido, recebimentos, ciclo de estados e atividade (chatter compras.pedido). */
export function DetalhePedido({ id, aoFechar }: { id: string | null; aoFechar: () => void }) {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  // Fase 2 (W8): no modo simplificado (ou sem Estoque, no recebimento) as ações do fluxo detalhado somem; a leitura fica.
  const s = useSessaoFluxo();
  const toast = useToast();
  const qc = useQueryClient();
  // Lote de 19/10 (item 9): o campo escondido em Configurações > Campos das telas some do detalhe (o dado continua).
  const tela = useCamposDaTela('compras.pedido');
  const q = useQuery({ queryKey: ['/compras/pedidos', 'obter', id], queryFn: () => get<PedidoDetalhe>(`/compras/pedidos/${id}`), enabled: !!id });
  const d = q.data;
  const [editando, setEditando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [recebendo, setRecebendo] = useState(false);

  const acao = (caminho: string, sucesso: string) => ({
    mutationFn: () => post(caminho),
    onSuccess: async () => {
      await invalidarCompras(qc);
      toast.sucesso(sucesso);
    },
    onError: (e: unknown) => toast.erro(mensagemDeErro(e)),
  });
  const enviar = useMutation(acao(`/compras/pedidos/${id}/enviar`, t('mensagens.pedidoEnviado')));
  const confirmar = useMutation(acao(`/compras/pedidos/${id}/confirmar`, t('mensagens.pedidoConfirmado')));
  const cancelar = useMutation({
    mutationFn: () => post(`/compras/pedidos/${id}/cancelar`, { motivo: motivo.trim() || undefined }),
    onSuccess: async () => {
      await invalidarCompras(qc);
      setCancelando(false);
      setMotivo('');
      toast.sucesso(t('mensagens.pedidoCancelado'));
    },
    onError: (e) => {
      setCancelando(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  const excluir = useMutation({
    mutationFn: () => del(`/compras/pedidos/${id}`),
    onSuccess: async () => {
      await invalidarCompras(qc);
      setExcluindo(false);
      toast.sucesso(tn('comum.removido'));
      aoFechar();
    },
    onError: (e) => {
      setExcluindo(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  const estado = d?.estado;
  const rodape = !d ? undefined : (
    <>
      {estado === 'rascunho' && s.pode('compras.excluir') && (
        <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setExcluindo(true)}>
          {tn('comum.excluir')}
        </Botao>
      )}
      {(estado === 'enviado' || estado === 'confirmado') && s.pode('compras.cancelar') && (
        <Botao variante="perigo" icone={<Ban className="size-4" />} onClick={() => setCancelando(true)}>
          {t('acoes.cancelarPedido')}
        </Botao>
      )}
      {estado === 'rascunho' && s.pode('compras.editar') && (
        <Botao icone={<Pencil className="size-4" />} onClick={() => setEditando(true)}>
          {tn('comum.editar')}
        </Botao>
      )}
      {/* Val5 (29/10): enviar (e confirmar) tem ação própria (`compras.enviar`). */}
      {estado === 'rascunho' && s.pode('compras.enviar') && (
        <Botao variante="primario" icone={<Send className="size-4" />} carregando={enviar.isPending} onClick={() => enviar.mutate()}>
          {t('acoes.enviarPedido')}
        </Botao>
      )}
      {estado === 'enviado' && s.pode('compras.enviar') && (
        <Botao variante="primario" icone={<Check className="size-4" />} carregando={confirmar.isPending} onClick={() => confirmar.mutate()}>
          {t('acoes.confirmarPedido')}
        </Botao>
      )}
      {(estado === 'confirmado' || estado === 'recebido_parcial') && s.pode('compras.receber') && (
        <Botao variante="primario" icone={<PackageCheck className="size-4" />} onClick={() => setRecebendo(true)}>
          {t('acoes.registrarRecebimento')}
        </Botao>
      )}
    </>
  );

  return (
    <>
      <Drawer
        aberto={!!id}
        aoFechar={aoFechar}
        largura="lg"
        titulo={d ? t('pedido.titulo', { numero: d.numero }) : t('pedido.tituloCurto')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.fechar')}
            </Botao>
            {rodape}
          </>
        }
      >
        {q.isPending && !!id && <Carregando />}
        {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
        {d && (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Selo cor={COR_PEDIDO[d.estado]}>{t(`estadoPedido.${d.estado}`)}</Selo>
              {d.atrasado && <Selo cor="laranja">{t('pedido.atrasadoDias', { count: d.dias_atraso })}</Selo>}
            </div>
            <p className="numero mb-1 text-3xl font-semibold text-tinta-1">{anunciarOcultos(dinheiro(d.total_centavos, d.moeda))}</p>
            <p className="mb-2 text-sm text-tinta-3">{t('pedido.totalDoPedido')}</p>
            {d.estado !== 'rascunho' && d.estado !== 'cancelado' && (
              <div className="mb-4 flex flex-col gap-1.5">
                <Progresso feito={d.recebido_centavos} total={d.total_centavos} />
                <p className="numero text-xs text-tinta-3">{t('pedido.recebidoDe', { feito: dinheiro(d.recebido_centavos, d.moeda), total: dinheiro(d.total_centavos, d.moeda) })}</p>
              </div>
            )}

            <dl className="divide-y divide-borda">
              <Linha rotulo={t('campos.fornecedor')}>
                <LinkParceiro id={d.fornecedor_id} nome={d.fornecedor_nome} />
              </Linha>
              {tela.visivel('condicao_pagamento') && <Linha rotulo={t('campos.condicao')}>{d.condicao_pagamento === 'avista' ? t('pedido.avista') : d.condicao_pagamento}</Linha>}
              {tela.visivel('prazo_entrega') && (
                <Linha rotulo={t('campos.prazoEntrega')}>
                  <span className={d.atrasado ? 'font-medium text-laranja' : undefined}>{d.prazo_entrega ? data(d.prazo_entrega) : ''}</span>
                </Linha>
              )}
              {tela.visivel('deposito_id') && <Linha rotulo={t('campos.deposito')}>{d.deposito_nome ?? t('pedido.depositoPadrao')}</Linha>}
              {d.requisicao_numero && (
                <Linha rotulo={t('campos.requisicao')}>
                  <Link to={`/operacao/compras/requisicoes?requisicao=${d.requisicao_id}`} className="text-acento-forte hover:underline" onClick={aoFechar}>
                    {d.requisicao_numero}
                  </Link>
                </Linha>
              )}
              {d.enviado_em && <Linha rotulo={t('campos.enviadoEm')}>{dataHora(d.enviado_em)}</Linha>}
              {d.confirmado_em && <Linha rotulo={t('campos.confirmadoEm')}>{dataHora(d.confirmado_em)}</Linha>}
              {d.recebido_em && <Linha rotulo={t('campos.recebidoEm')}>{dataHora(d.recebido_em)}</Linha>}
              {d.cancelado_em && <Linha rotulo={t('campos.canceladoEm')}>{dataHora(d.cancelado_em)}</Linha>}
              {d.motivo_cancelamento && <Linha rotulo={t('campos.motivoCancelamento')}>{textoLivre(d.motivo_cancelamento)}</Linha>}
              {d.observacoes && tela.visivel('observacoes') && <Linha rotulo={t('campos.observacoes')}>{textoLivre(d.observacoes)}</Linha>}
            </dl>

            <Secao titulo={t('secoes.itens')}>
              <ul className="divide-y divide-borda rounded-lg border border-borda">
                {d.itens_detalhe.map((i) => (
                  <li key={i.id} className="flex min-w-0 flex-col gap-1.5 px-3 py-2 text-sm">
                    <span className="flex min-w-0 items-start justify-between gap-3">
                      <span className="min-w-0">
                        <span className="block truncate" title={i.produto_nome ?? undefined}>{nomeCadastro(i.produto_nome)}</span>
                        <TextoCortado className="numero block text-xs text-tinta-3">
                          {i.produto_codigo} · {qtd(i.quantidade, numero)} {i.unidade ?? ''} x {anunciarOcultos(dinheiro(i.preco_unitario_centavos, d.moeda))}
                        </TextoCortado>
                      </span>
                      <span className="numero shrink-0">{anunciarOcultos(dinheiro(i.total_centavos, d.moeda))}</span>
                    </span>
                    {d.estado !== 'rascunho' && d.estado !== 'cancelado' && (
                      <span className="flex flex-col gap-1">
                        <Progresso feito={i.quantidade_recebida} total={i.quantidade} />
                        <span className="numero text-xs text-tinta-3">{t('pedido.itemRecebido', { feito: qtd(i.quantidade_recebida, numero), total: qtd(i.quantidade, numero), unidade: i.unidade ?? '' })}</span>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Secao>

            <Secao titulo={t('secoes.recebimentos')}>
              {d.recebimentos.length === 0 ? (
                <p className="text-sm text-tinta-3">{t('pedido.semRecebimentos')}</p>
              ) : (
                <ul className="divide-y divide-borda rounded-lg border border-borda">
                  {d.recebimentos.map((r) => (
                    <li key={r.id} className="flex min-w-0 items-center justify-between gap-3 px-3 py-2 text-sm">
                      <Link to={`/operacao/compras/recebimentos?recebimento=${r.id}`} className="min-w-0 text-acento-forte hover:underline" onClick={aoFechar}>
                        <TextoCortado className="block">
                          {r.numero}
                        </TextoCortado>
                      </Link>
                      <span className="flex shrink-0 items-center gap-3 text-xs text-tinta-3">
                        <span className="numero">{data(r.data)}</span>
                        <span>{t('recebimento.contasGeradas', { count: r.documentos.length })}</span>
                        <span className="numero text-sm text-tinta-1">{anunciarOcultos(dinheiro(r.total_centavos, d.moeda))}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Secao>

            <div className="mt-6 border-t border-borda pt-4">
              <Chatter entidade="compras.pedido" registroId={d.id} titulo={t('pedido.titulo', { numero: d.numero })} rotulosCampos={{ estado: t('campos.estado'), condicao_pagamento: t('campos.condicao'), prazo_entrega: t('campos.prazoEntrega'), total_centavos: t('campos.total'), observacoes: t('campos.observacoes'), motivo_cancelamento: t('campos.motivoCancelamento') }} />
            </div>
          </>
        )}
      </Drawer>

      <FormPedido aberto={editando && !!d} pedido={d ?? null} aoFechar={() => setEditando(false)} />
      <ModalRecebimento pedidoId={recebendo ? id : null} aoFechar={() => setRecebendo(false)} />
      <Confirmar aberto={excluindo} aoFechar={() => setExcluindo(false)} aoConfirmar={() => excluir.mutate()} carregando={excluir.isPending} perigoso titulo={t('pedido.excluirTitulo')} texto={t('pedido.excluirTexto')} />
      <Modal
        aberto={cancelando}
        aoFechar={() => setCancelando(false)}
        titulo={t('pedido.cancelarTitulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setCancelando(false)}>
              {tn('comum.voltar')}
            </Botao>
            <Botao variante="perigo" carregando={cancelar.isPending} onClick={() => cancelar.mutate()}>
              {t('acoes.cancelarPedido')}
            </Botao>
          </>
        }
      >
        <p className="mb-3 text-sm text-tinta-2">{t('pedido.cancelarTexto')}</p>
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('campos.motivoCancelamento')}
          <AreaSensivel rows={3} maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco" />
        </label>
      </Modal>
    </>
  );
}
